import { ExternalLink, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { landingContent } from '../content';
import { site } from '../content/shared';
import type { Locale } from '../content/types';
import { localizedPaths, sectionIds } from '../lib/routes';
import { ButtonLink } from './Button';
import { Container } from './Container';
import { LanguageSwitcher } from './LanguageSwitcher';

type HeaderProps = {
  locale: Locale;
};

export function Header({ locale }: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const content = landingContent[locale];
  const basePath = localizedPaths.landing[locale];
  const navItems = [
    { label: content.nav.services, href: `${basePath}#${sectionIds.services}` },
    { label: content.nav.solutions, href: `${basePath}#${sectionIds.solutions}` },
    { label: content.nav.process, href: `${basePath}#${sectionIds.process}` },
    { label: content.nav.experience, href: `${basePath}#${sectionIds.experience}` },
    { label: content.nav.faq, href: `${basePath}#${sectionIds.faq}` },
  ];

  return (
    <header className="site-header">
      <Container className="site-header__inner">
        <Link className="wordmark" to={basePath} aria-label="Maxim Domentii home">
          <span className="wordmark__mark" aria-hidden="true">
            MD
          </span>
          <span>Maxim Domentii</span>
        </Link>

        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>

        <div className="site-header__actions">
          <div className="desktop-header-controls desktop-only">
            <LanguageSwitcher currentLocale={locale} />
            <a
              className="icon-link"
              href={site.linkedin}
              aria-label={`${content.nav.linkedin}. ${content.common.externalLink}`}
              rel="noreferrer"
              target="_blank"
            >
              <ExternalLink aria-hidden="true" size={18} />
            </a>
          </div>
          <ButtonLink className="desktop-cta desktop-only" href={`${basePath}#${sectionIds.contact}`}>
            {content.nav.contact}
          </ButtonLink>
          <button
            className="menu-button"
            type="button"
            aria-label={isOpen ? content.nav.close : content.nav.menu}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
            onClick={() => setIsOpen((value) => !value)}
          >
            {isOpen ? <X aria-hidden="true" size={22} /> : <Menu aria-hidden="true" size={22} />}
          </button>
        </div>
      </Container>

      <div className={`mobile-nav ${isOpen ? 'mobile-nav--open' : ''}`} id="mobile-navigation">
        <Container>
          <nav aria-label="Mobile navigation">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setIsOpen(false)}>
                {item.label}
              </a>
            ))}
            <a href={`${basePath}#${sectionIds.contact}`} onClick={() => setIsOpen(false)}>
              {content.nav.contact}
            </a>
          </nav>
          <div className="mobile-nav__controls">
            <LanguageSwitcher currentLocale={locale} onNavigate={() => setIsOpen(false)} />
            <a
              className="mobile-nav__external"
              href={site.linkedin}
              rel="noreferrer"
              target="_blank"
              onClick={() => setIsOpen(false)}
            >
              <ExternalLink aria-hidden="true" size={16} />
              {content.nav.linkedin}
            </a>
          </div>
        </Container>
      </div>
    </header>
  );
}
